import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function ExitStrategy() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exitData, setExitData] = useState(null);
  const [hasMarketingReport, setHasMarketingReport] = useState(false);

  useEffect(() => {
    fetchExitData();
  }, []);

  const fetchExitData = async () => {
    try {
      // Fetch exit strategy data
      const exitRes = await fetch('/api/exit-strategy/analysis', {
        credentials: 'include'
      });
      if (exitRes.ok) {
        const data = await exitRes.json();
        if (data.exit_path) {
          // Handle old data format where exit_path might be a JSON object string
          let exitPath = data.exit_path;
          let targetAmount = data.target_amount;
          let targetDate = data.target_date;

          // Check if exit_path is a JSON string (old format)
          if (typeof exitPath === 'string' && exitPath.startsWith('{')) {
            try {
              const parsed = JSON.parse(exitPath);
              exitPath = parsed.path || exitPath;
              // Also extract target info if present in old format
              if (parsed.target_amount && !targetAmount) targetAmount = parsed.target_amount;
              if (parsed.timeline && !targetDate) targetDate = parsed.timeline;
            } catch (e) {
              // Not JSON, use as-is
            }
          }

          setExitData({
            ...data,
            exit_path: exitPath,
            target_amount: targetAmount,
            target_date: targetDate
          });
        }
      }

      // Check if marketing report exists (uses same endpoint as UpdatedExitStrategy page)
      const marketingRes = await fetch('/api/marketing/get-exit-strategy-report', {
        credentials: 'include'
      });
      if (marketingRes.ok) {
        const marketingData = await marketingRes.json();
        // Report is available if we get data back (the endpoint returns report data directly)
        if (marketingData && !marketingData.error) {
          setHasMarketingReport(true);
        }
      }
    } catch (err) {
      console.error('Error fetching exit data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getExitPathLabel = (path) => {
    const labels = {
      'lifestyle': 'Lifestyle Business (Keep Forever)',
      'build_to_sell': 'Build to Sell (3-7 Year Exit)',
      'acquisition': 'Scale for Acquisition',
      'legacy': 'Legacy Business (Multi-Generational)'
    };
    return labels[path] || path;
  };

  const getExitPathIcon = (path) => {
    const icons = {
      'lifestyle': '🌴',
      'build_to_sell': '🎯',
      'acquisition': '🚀',
      'legacy': '🏛️'
    };
    return icons[path] || '📊';
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading your exit strategy...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4">
      {/* Header */}
      <div className="mb-4">
        <h1>End Game / Exit Strategy</h1>
        <p className="text-muted mb-0">
          Plan your ultimate business outcome. Life changes - revisit and update your strategy as needed.
        </p>
      </div>

      {/* Current Strategy Summary */}
      {exitData && (
        <Alert variant="light" className="border mb-4" style={{ backgroundColor: '#f8f9fa' }}>
          <Row className="align-items-center">
            <Col>
              <div className="d-flex align-items-center">
                <span style={{ fontSize: '2rem', marginRight: '1rem' }}>
                  {getExitPathIcon(exitData.exit_path)}
                </span>
                <div>
                  <strong>Current Strategy:</strong> {getExitPathLabel(exitData.exit_path)}
                  {exitData.target_amount && (
                    <div className="text-muted small">
                      Target: {exitData.target_amount}
                      {exitData.target_date && ` by ${exitData.target_date}`}
                    </div>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Alert>
      )}

      <Row>
        {/* Edit/Setup Exit Strategy Card */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h5 className="mb-0">
                <span className="me-2">⚙️</span>
                {exitData ? 'Edit Exit Strategy' : 'Set Up Exit Strategy'}
              </h5>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Card.Text className="text-muted flex-grow-1">
                {exitData ? (
                  <>
                    Life changes - maybe you have new family goals, want to retire earlier,
                    or your business has grown beyond expectations. Update your exit path
                    and financial targets anytime.
                  </>
                ) : (
                  <>
                    Choose your exit path (lifestyle business vs. build to sell),
                    set your financial targets, and generate a personalized strategy
                    based on your business data.
                  </>
                )}
              </Card.Text>
              <div className="mt-3">
                <strong className="d-block mb-2">Options:</strong>
                <ul className="small text-muted mb-3">
                  <li>Lifestyle Business - maximize ongoing profit</li>
                  <li>Build to Sell - maximize exit value</li>
                  <li>Set financial targets and timeline</li>
                </ul>
              </div>
              <Button
                variant="dark"
                style={{ backgroundColor: colors.greyDarkest }}
                onClick={() => navigate('/exit-strategy?edit=true&from=cc')}
              >
                {exitData ? 'Update Strategy' : 'Get Started'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* View Exit Strategy Report Card */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">📋</span>
                  Your Exit Strategy
                </h5>
                {exitData && (
                  <Badge bg="success" style={{ fontSize: '0.7rem' }}>Generated</Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Card.Text className="text-muted flex-grow-1">
                {exitData ? (
                  <>
                    View your personalized exit strategy analysis including your chosen path,
                    financial targets, and AI-generated recommendations based on your
                    business fundamentals.
                  </>
                ) : (
                  <>
                    Once you set up your exit strategy, you'll be able to view your
                    personalized analysis here.
                  </>
                )}
              </Card.Text>
              {exitData && (
                <div className="mt-3 mb-3">
                  <strong className="d-block mb-2">Includes:</strong>
                  <ul className="small text-muted mb-0">
                    <li>Exit path analysis</li>
                    <li>Financial projections</li>
                    <li>Strategic recommendations</li>
                  </ul>
                </div>
              )}
              <Button
                variant={exitData ? 'outline-dark' : 'outline-secondary'}
                disabled={!exitData}
                onClick={() => navigate('/exit-strategy?from=cc')}
              >
                {exitData ? 'View Report' : 'Not Available Yet'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Marketing Strategy & Exit Valuation Report Card */}
        <Col lg={4} className="mb-4">
          <Card className="h-100 shadow-sm">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <span className="me-2">📈</span>
                  Marketing & Valuation
                </h5>
                {hasMarketingReport && (
                  <Badge bg="success" style={{ fontSize: '0.7rem' }}>Generated</Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body className="d-flex flex-column">
              <Card.Text className="text-muted flex-grow-1">
                {hasMarketingReport ? (
                  <>
                    Comprehensive analysis showing how your marketing strategy impacts
                    business valuation and exit readiness. Includes revenue projections
                    and gap analysis.
                  </>
                ) : (
                  <>
                    After completing your marketing strategy (upgrade paths, promotions, etc.),
                    this report shows how it all impacts your exit valuation.
                  </>
                )}
              </Card.Text>
              {hasMarketingReport && (
                <div className="mt-3 mb-3">
                  <strong className="d-block mb-2">Includes:</strong>
                  <ul className="small text-muted mb-0">
                    <li>Revenue projections by month</li>
                    <li>Valuation impact analysis</li>
                    <li>Gap to target tracking</li>
                    <li>Strategic recommendations</li>
                  </ul>
                </div>
              )}
              <Button
                variant={hasMarketingReport ? 'outline-dark' : 'outline-secondary'}
                disabled={!hasMarketingReport}
                onClick={() => navigate('/updated-exit-strategy?from=cc')}
              >
                {hasMarketingReport ? 'View Report' : 'Not Available Yet'}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Info Section */}
      <Row className="mt-2">
        <Col>
          <Card className="border-0" style={{ backgroundColor: '#fff8e6' }}>
            <Card.Body>
              <h6 className="mb-2" style={{ color: colors.goldDark }}>
                <span className="me-2">💡</span>
                Why Review Your Exit Strategy?
              </h6>
              <p className="small text-muted mb-0">
                Your exit strategy isn't set in stone. Major life events - new children,
                health changes, market shifts, or simply changing priorities - are all
                good reasons to revisit your goals. The best time to update your strategy
                is before you need to act on it.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ExitStrategy;
